import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';
import { Copy, Pencil, Trash2 } from 'lucide-react';

export interface ProductBulkActionFooterProps {
  selectedCount: number;
  onBulkUpdate: () => void;
  onBulkDelete: () => void;
  onClearSelection: () => void;
  /**
   * QA-88: 선택 복사(일괄). 선택한 상품마다 복사본(미노출 DRAFT)을 목록에 바로 생성한다.
   * 미지정 시 복사 버튼을 렌더하지 않는다(점진 도입 안전).
   */
  onBulkCopy?: () => void;
  disabled?: boolean;
}

/**
 * ID88: 상품 다중 선택 시 노출되는 sticky 일괄 액션 바. orders 의 BulkActionFooter 와 동형 —
 * 선택 0건이면 렌더하지 않는다. 일괄삭제/일괄복사는 상위에서 선택 id 마다 단건 호출을 순차 실행한다.
 */
export const ProductBulkActionFooter = ({
  selectedCount,
  onBulkUpdate,
  onBulkDelete,
  onClearSelection,
  onBulkCopy,
  disabled,
}: ProductBulkActionFooterProps) => {
  const { t } = useTranslation('products');
  if (selectedCount === 0) return null;

  return (
    <div
      role="region"
      aria-label={t('products.bulk.label', { defaultValue: '선택 상품 일괄 작업' })}
      className="sticky bottom-4 z-10 flex items-center gap-3 rounded-xl px-4 py-3 shadow"
      style={{ background: 'var(--surface-raised)' }}
      data-testid="products-bulk-footer"
    >
      <span data-testid="products-bulk-count" className="sr-only">
        {selectedCount}
      </span>
      <span className="text-sm text-[color:var(--content-strong)]">
        {t('products.bulk.selected', { count: selectedCount, defaultValue: `${selectedCount}건 선택됨` })}
      </span>
      {onBulkCopy ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onBulkCopy}
          disabled={disabled}
          data-testid="products-bulk-copy"
        >
          <Copy size={14} aria-hidden="true" />
          {t('products.bulk.copy', { defaultValue: '선택 복사' })}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="cta"
        size="sm"
        onClick={onBulkUpdate}
        disabled={disabled}
        data-testid="products-bulk-update"
      >
        <Pencil size={14} aria-hidden="true" />
        {t('products.bulk.update', { defaultValue: '일괄 변경' })}
      </Button>
      <Button
        type="button"
        variant="danger"
        size="sm"
        onClick={onBulkDelete}
        disabled={disabled}
        data-testid="products-bulk-delete"
      >
        <Trash2 size={14} aria-hidden="true" />
        {t('products.bulk.delete', { defaultValue: '선택 삭제' })}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        disabled={disabled}
        className="ml-auto"
      >
        {t('products.bulk.clear', { defaultValue: '선택 해제' })}
      </Button>
    </div>
  );
};
